/**
 * SaveIndicator - Subtle visual confirmation for autosave status
 * 
 * Shows a small, non-intrusive indicator that doesn't interrupt workflow.
 * Positioned in a corner of the note editor.
 */

import React from 'react';
import { Check, Cloud, CloudOff, Loader2 } from 'lucide-react';
import { SaveStatus } from '../hooks/useNoteAutosave';

/**
 * Format time as absolute timestamp (e.g., "at 10:15 AM")
 * Shows "just now" for very recent saves, then switches to clock time
 */
const formatSaveTime = (date) => {
  if (!date) return '';
  
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  
  // Show "just now" for saves within last 5 seconds
  if (diffSec < 5) return 'just now';
  
  // Otherwise show the actual time for peace of mind
  return `at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

export function SaveIndicator({ 
  status, 
  lastSavedAt,
  className = '',
  showTimestamp = true,
}) {
  const getStatusConfig = () => {
    switch (status) {
      case SaveStatus.SAVING:
        return {
          icon: <Loader2 className="h-3 w-3 animate-spin" />,
          text: 'Saving...',
          className: 'text-blue-500',
        };
      case SaveStatus.SAVED:
        return {
          icon: <Check className="h-3 w-3" />,
          text: showTimestamp && lastSavedAt 
            ? `Saved ${formatSaveTime(lastSavedAt)}`
            : 'Saved',
          className: 'text-green-600',
        };
      case SaveStatus.ERROR:
        return {
          icon: <CloudOff className="h-3 w-3" />,
          text: 'Save failed',
          className: 'text-red-500',
        };
      case SaveStatus.OFFLINE_CACHED:
        return {
          icon: <Cloud className="h-3 w-3" />,
          text: 'Cached locally',
          className: 'text-amber-500',
        };
      case SaveStatus.IDLE:
      default:
        // Show "Auto-save on" when idle to indicate autosave is active
        return {
          icon: <Cloud className="h-3 w-3" />,
          text: lastSavedAt ? `Saved ${formatSaveTime(lastSavedAt)}` : 'Auto-save on',
          className: lastSavedAt ? 'text-green-600' : 'text-gray-400',
        };
    }
  };
  
  const config = getStatusConfig();
  
  return (
    <div 
      className={`
        inline-flex items-center gap-1.5 
        text-xs font-medium
        transition-all duration-300 ease-in-out
        ${config.className}
        ${className}
      `}
      role="status"
      aria-live="polite"
    >
      {config.icon}
      <span className="select-none">{config.text}</span>
    </div>
  );
}

/**
 * Floating Save Indicator - Positioned version for note editors
 */
export function FloatingSaveIndicator({
  status,
  lastSavedAt,
  position = 'bottom-right', // 'top-right', 'bottom-right', 'top-left', 'bottom-left'
}) {
  const positionClasses = {
    'top-right': 'top-2 right-2',
    'bottom-right': 'bottom-2 right-2',
    'top-left': 'top-2 left-2',
    'bottom-left': 'bottom-2 left-2',
  };
  
  return (
    <div 
      className={`
        absolute ${positionClasses[position]}
        bg-white/90 backdrop-blur-sm
        px-2 py-1 rounded-md
        shadow-sm border border-gray-100
        z-10
      `}
    >
      <SaveIndicator 
        status={status} 
        lastSavedAt={lastSavedAt}
        showTimestamp={true}
      />
    </div>
  );
}

export default SaveIndicator;
